(() => {
    'use strict';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let current, platformPromise;
    window.isPotentialCompanyBrowser = () => platformPromise ||= window.facilityLandingPlatformReady || Promise.resolve().then(() =>
        typeof window.getAppPlatform === 'function' ? window.getAppPlatform() : 'unknown'
    ).then(platform => String(platform).toLowerCase() === 'web').catch(() => false);
    const escape = value => { const node = document.createElement('span'); node.textContent = value; return node.innerHTML; };
    const active = state => current === state && state.root.isConnected;
    const dayFormat = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });
    const formatDay = iso => {
        const [year, month, day] = String(iso).split('-').map(Number);
        return year && month && day ? dayFormat.format(new Date(year, month - 1, day)) : String(iso);
    };
    // Visual grouping only; the server order of the slots is never changed.
    const period = time => {
        const hour = Number(String(time).slice(0, 2));
        return hour >= 18 ? ['Akşam', 'fa-moon'] : hour >= 12 ? ['Öğleden sonra', 'fa-cloud-sun'] : hour >= 6 ? ['Sabah', 'fa-sun'] : ['Gece', 'fa-star'];
    };
    // The "ticket" repeats the guest's choice in both the sign-in and the post-sign-in dialogs.
    function ticketHtml(selection) {
        const head = `<div class="pl-ticket-head"><span class="pl-ticket-icon"><i class="fa-solid ${selection.icon || 'fa-futbol'}" aria-hidden="true"></i></span><div><small>${escape(selection.label || 'Maç seçimin')}</small><strong>${escape(selection.title || '')}</strong></div></div>`;
        const rows = (selection.rows || []).filter(row => row[1]);
        return head + (rows.length ? `<dl class="pl-ticket-grid">${rows.map(([name, value]) => `<div><dt>${escape(name)}</dt><dd>${escape(value)}</dd></div>`).join('')}</dl>` : '');
    }
    async function request(state, path, values = {}, method = 'GET', asHtml = false, signal) {
        const token = state.root.querySelector('input[name="__RequestVerificationToken"]')?.value;
        const body = new URLSearchParams(values);
        const response = await fetch(path + (method === 'GET' ? '?' + body : ''), {
            method, credentials: 'same-origin', cache: 'no-store', signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest', ...(method !== 'GET' ? {
                'Content-Type': 'application/x-www-form-urlencoded', RequestVerificationToken: token || ''
            } : {}) }, ...(method !== 'GET' ? { body } : {})
        });
        if (!response.ok) throw new Error(response.status === 409 ? 'Seçtiğin saat değişti. Takvimi yenileyip tekrar dene.' : 'İşlem tamamlanamadı. Lütfen yeniden dene.');
        return asHtml ? response.text() : response.status === 204 || response.headers.get('content-length') === '0' ? null : response.json();
    }
    function dispose() {
        if (!current) return;
        current.request?.abort(); current.events.abort();
        current.reveal?.disconnect(); current.sections?.disconnect();
        (current.observers || []).forEach(observer => observer.disconnect());
        cancelAnimationFrame(current.frame);
        clearInterval(current.popupTimer); clearTimeout(current.closedTimer);
        current.root.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
        current.root.classList.remove('pl-motion', 'is-away', 'is-scrolled');
        current = null;
    }
    function setupDialogs(state) {
        const signal = state.events.signal;
        state.root.addEventListener('click', event => {
            const close = event.target.closest('[data-pl-close]');
            if (close) close.closest('dialog').close();
        }, { signal });
        state.root.querySelectorAll('dialog').forEach(dialog => {
            dialog.addEventListener('click', event => {
                if (event.target !== dialog) return;
                const bounds = dialog.getBoundingClientRect();
                if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
            }, { signal });
        });
    }
    const selectedField = root => root.querySelector('[data-pl-field-choice][aria-pressed="true"]');
    function chooseField(state, button) {
        state.root.querySelectorAll('[data-pl-field-choice]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    }
    function skeleton() {
        return '<div class="pl-skeleton" role="status" aria-label="Saha takvimi yükleniyor">' + '<span></span>'.repeat(8) + '</div>';
    }
    function fitCalendar(state) {
        if (!active(state)) return;
        const root = state.root, area = root.querySelector('[data-pl-slots]');
        if (!area) return;
        const slots = Array.from(area.querySelectorAll('.pl-slot'));
        if (!slots.length) return;
        let pager = area.querySelector('.pl-calendar-pager');
        if (!window.matchMedia('(min-width: 1024px)').matches) {
            slots.forEach(slot => slot.hidden = false);
            area.querySelectorAll('.pl-slot-group').forEach(group => group.hidden = false);
            pager?.remove(); state.slotPage = 0; state.slotPageSize = null; return;
        }
        const card = root.querySelector('.pl-booking'), inner = root.querySelector('.pl-hero-inner');
        const style = getComputedStyle(inner);
        const available = inner.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        const desiredPage = state.slotPage || 0;
        let pageSize = state.slotPageSize || Math.min(24, Math.ceil(slots.length / 4) * 4), page = 0;
        function show(requestedPage) {
            const pages = Math.ceil(slots.length / pageSize);
            page = Math.max(0, Math.min(requestedPage, pages - 1));
            slots.forEach((slot, index) => slot.hidden = index < page * pageSize || index >= (page + 1) * pageSize);
            area.querySelectorAll('.pl-slot-group').forEach(group => group.hidden = !Array.from(group.querySelectorAll('.pl-slot')).some(slot => !slot.hidden));
            if (pages === 1) { pager?.remove(); pager = null; return; }
            if (!pager) { pager = document.createElement('nav'); pager.className = 'pl-calendar-pager'; pager.setAttribute('aria-label', 'Diğer maç saatleri'); area.append(pager); }
            pager.innerHTML = `<button type="button" data-pl-slot-page="${page - 1}" ${page === 0 ? 'disabled' : ''} aria-label="Önceki saatler"><i class="fa-solid fa-chevron-left" aria-hidden="true"></i>Önceki</button><span aria-live="polite">${page + 1} / ${pages}</span><button type="button" data-pl-slot-page="${page + 1}" ${page === pages - 1 ? 'disabled' : ''} aria-label="Sonraki saatler">Sonraki<i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button>`;
        }
        if (!state.slotPageSize) {
            // Fit the tallest page, then keep the same page size for Previous/Next.
            // Different hour groups must never change the total page count mid-navigation.
            while (true) {
                let fits = true;
                for (let candidate = 0; candidate < Math.ceil(slots.length / pageSize); candidate++) {
                    show(candidate);
                    if (card.scrollHeight > available + 1) { fits = false; break; }
                }
                if (fits || pageSize <= 4) break;
                pageSize -= 4;
            }
            state.slotPageSize = pageSize;
        }
        show(desiredPage);
        state.slotPage = page;
    }
    async function loadCalendar(state) {
        const root = state.root, area = root.querySelector('[data-pl-slots]'), summary = root.querySelector('[data-pl-summary]');
        const choice = selectedField(root);
        if (!area || !choice) return;
        state.request?.abort();
        const controller = state.request = new AbortController();
        area.setAttribute('aria-busy', 'true');
        state.slotPage = 0; state.slotPageSize = null;
        area.innerHTML = skeleton();
        summary.textContent = '';
        const dayButton = root.querySelector('[data-pl-day][aria-pressed="true"]');
        const date = dayButton.dataset.plDay;
        const fieldId = choice.dataset.plFieldChoice;
        try {
            const slots = await request(state, '/Field/LandingCalendar', { id: root.dataset.companyId, fieldId, date }, 'GET', false, controller.signal);
            if (!active(state) || controller.signal.aborted) return;
            area.innerHTML = '';
            const fieldName = choice.textContent.trim();
            let group, groupName, index = 0;
            slots.forEach(slot => {
                const [name, icon] = period(slot.time);
                if (name !== groupName) {
                    groupName = name;
                    const section = document.createElement('div');
                    section.className = 'pl-slot-group';
                    section.innerHTML = `<p class="pl-slot-period"><i class="fa-solid ${icon}" aria-hidden="true"></i>${escape(name)}</p><div class="pl-slot-list"></div>`;
                    area.append(section); group = section.querySelector('.pl-slot-list');
                }
                const button = document.createElement('button');
                button.type = 'button'; button.className = 'pl-slot is-' + slot.status;
                button.disabled = !slot.selectable;
                button.style.setProperty('--i', String(index++));
                const label = slot.status === 'reservation' ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(slot.price)
                    : slot.status === 'busy' ? 'Dolu' : slot.status === 'closed' ? 'Kapalı' : 'Uygunluğu öğren';
                button.setAttribute('aria-label', `${slot.time}, ${label}`);
                button.innerHTML = `<strong>${escape(slot.time)}</strong><small>${escape(label)}</small>${slot.selectable ? '<i class="fa-solid fa-arrow-right" aria-hidden="true"></i>' : ''}`;
                if (slot.selectable) button.addEventListener('click', () => openAuth(state, {
                    id: root.dataset.companyId, action: 'slot', fieldId, date, time: slot.time
                }, {
                    label: 'Seçtiğin maç saati', title: state.facilityName, icon: 'fa-futbol',
                    rows: [['Saha', fieldName], ['Gün', formatDay(date)], ['Saat', slot.time]]
                }), { signal: state.events.signal });
                group.append(button);
            });
            const open = slots.filter(slot => slot.selectable).length, taken = slots.filter(slot => slot.status === 'busy' || slot.status === 'closed').length;
            if (!slots.length || !open) {
                const last = root.querySelector('[data-pl-day]:last-of-type') === dayButton;
                area.innerHTML = `<div class="pl-slots-empty"><i class="fa-regular fa-calendar" aria-hidden="true"></i><p>${slots.length ? 'Bu gün için seçilebilecek saat yok.' : 'Bu gün için seçilebilecek saat kalmadı.'} Başka bir gün seçebilirsin.</p>${last ? '' : '<button type="button" class="pl-btn pl-btn--lime pl-btn--sm" data-pl-next-day><em>Sonraki güne bak</em><i class="fa-solid fa-arrow-right" aria-hidden="true"></i></button>'}</div>`;
            } else summary.textContent = `${formatDay(date)} · ${open} saat seçilebilir${taken ? ` · ${taken} dolu` : ''}`;
        } catch (error) {
            if (error.name === 'AbortError' || !active(state)) return;
            area.innerHTML = '<div class="pl-error" role="alert"><p>Takvim şu anda yüklenemedi.</p><button type="button" class="pl-btn pl-btn--lime pl-btn--sm" data-pl-calendar-retry><em>Yeniden dene</em></button></div>';
        } finally { if (active(state) && !controller.signal.aborted) { area.setAttribute('aria-busy', 'false'); fitCalendar(state); } }
    }
    function openAuth(state, intent, selection) {
        state.intent = intent;
        const root = state.root, dialog = root.querySelector('.pl-auth-dialog');
        root.querySelector('[data-pl-auth-selection]').innerHTML = ticketHtml(selection);
        root.querySelector('[data-pl-auth-copy]').textContent = intent.kind ? 'Seçtiğin işleme devam etmek için giriş yap veya Google / Apple ile ücretsiz hesabını oluştur.' : intent.action === 'contact'
            ? 'Tesisin iletişim bilgilerini görmek için giriş yap veya ücretsiz hesabını oluştur. Devam etmeden önce üyelik bilgilerini tamamlamalısın.'
            : intent.action === 'directions' ? 'Tesise yol tarifi almak için giriş yap veya ücretsiz hesabını oluştur. Devam etmeden önce üyelik bilgilerini tamamlamalısın.'
            : 'Seçtiğin saatin rezervasyon seçeneklerini görmek ve maçını planlamak için giriş yap veya ücretsiz hesabını oluştur.';
        root.querySelector('[data-pl-auth-error]').textContent = '';
        root.querySelector('[data-pl-auth-status]').textContent = '';
        dialog.showModal();
    }
    // Called only by the two provider buttons on the guest browser surface.
    window.startFacilitySocialAuth = async (element, provider) => {
        const state = current;
        if (!state?.intent || state.authBusy) return;
        state.authBusy = true;
        const status = state.root.querySelector('[data-pl-auth-status]'), failure = state.root.querySelector('[data-pl-auth-error]');
        const buttons = [...state.root.querySelectorAll('[data-pl-provider]')];
        buttons.forEach(button => button.disabled = true);
        element.classList.add('is-loading');
        failure.textContent = ''; status.textContent = provider + ' ile bağlanılıyor…';
        // Open while the click still has browser activation (especially Safari).
        const popup = window.open('about:blank', 'facility' + provider + 'Sign', 'width=500,height=700,scrollbars=yes');
        const reset = () => {
            if (!active(state)) return;
            state.authBusy = false; element.classList.remove('is-loading');
            buttons.forEach(button => button.disabled = false);
        };
        try {
            if (!await window.isPotentialCompanyBrowser()) throw new Error('Tarayıcı girişi kullanılamıyor.');
            await request(state, state.intent.kind ? '/ProfileLanding/Prepare' : '/Field/PrepareLanding', state.intent, 'POST');
            const url = await (provider === 'Google' ? window.buildGoogleAuthUrl(true) : window.buildAppleAuthUrl(true));
            if (!active(state)) { popup?.close(); return; }
            if (!popup) { location.assign(url); return; }
            popup.location.replace(url);
            status.textContent = `${provider} penceresinde girişi tamamla. Ardından ${state.intent.kind ? 'seçtiğin işleme' : state.intent.action === 'directions' ? 'yol tarifine' : state.intent.action === 'contact' ? 'tesisin iletişim bilgilerine' : 'seçtiğin saate'} devam edeceksin.`;
            clearInterval(state.popupTimer);
            state.popupTimer = setInterval(() => {
                if (!popup.closed) return;
                clearInterval(state.popupTimer); reset();
                // The sign-in page closes its own window right before this page continues, so wait before suggesting a retry.
                clearTimeout(state.closedTimer);
                state.closedTimer = setTimeout(() => { if (active(state)) status.textContent = 'Giriş penceresi kapandı. Giriş tamamlandıysa bu sayfa devam eder; olmadıysa tekrar deneyebilirsin.'; }, 1800);
            }, 500);
        } catch (error) {
            popup?.close(); reset(); status.textContent = '';
            if (active(state)) failure.textContent = error.message;
        }
    };
    async function resume(state) {
        const root = state.root, dialog = root.querySelector('dialog'), content = root.querySelector('[data-pl-resume-content]');
        const title = root.querySelector('[data-pl-resume-title]'), kicker = root.querySelector('[data-pl-resume-kicker]');
        const companyId = root.dataset.companyId;
        const consume = () => request(state, '/Field/ConsumeLanding', {}, 'POST').catch(() => {});
        try {
            const data = await request(state, '/Field/LandingAction', { id: companyId });
            if (!active(state)) return;

            if (data.action === 'directions') {
                const locationTab = document.querySelector('.potential-company-legacy [data-group="CompanyMainGroup"][data-id="div4"]');
                if (!locationTab || !window.GlobalTabs) throw new Error('Tesisin konum sekmesi yüklenemedi.');
                window.GlobalTabs.init(document);
                window.GlobalTabs.select(locationTab);
                locationTab.click();
                await consume(); return;
            }
            if (data.action === 'contact') {
                if (data.potential) {
                    const contact = [...document.querySelectorAll('.potential-company-legacy .ContactModalPotantielCompany')]
                        .find(element => element.dataset.id === companyId);
                    if (!contact) throw new Error('Tesisin iletişim ekranı yüklenemedi.');
                    contact.click();
                }
                await consume(); return;
            }
            if (data.action === 'reservation') {
                // The existing reservation dialog revalidates and applies the existing profile/payment rules.
                if (typeof window.openReservationModal !== 'function') throw new Error('Rezervasyon ekranı yüklenemedi.');
                await window.openReservationModal(data.time, data.fieldId, data.date);
                await consume(); return;
            }
            if (data.action === 'match') {
                if (typeof window.CreateMatchPopUp !== 'function') throw new Error('Maç ekranı yüklenemedi.');
                await window.CreateMatchPopUp(0, 0, data.districtId, data);
                await consume(); return;
            }
            const description = data.action === 'unavailable' ? 'Bu saat şu anda seçilemiyor. Tesis takviminden farklı bir saat seçebilirsin.'
                : data.action === 'profile' ? 'Seçtiğin saatte rezervasyona devam etmek için profilindeki gerekli bilgileri tamamla. Ardından aynı saate döneceksin.'
                : data.potential ? `${data.companyName}, Sahasenin ile henüz anlaşmalı değildir. Yine de ücretsiz maç ilanı yayınlayabilir, takımını kurabilir, oyuncu arayabilir ve istatistiklerini tutabilirsin. Maçın kesinliği için tesisle ayrıca görüşmelisin.`
                : 'Bu saat için Sahasenin üzerinden online rezervasyon açık değildir. Tesisle görüşebilir; ücretsiz maç ilanı yayınlayabilir, takımını kurabilir ve oyuncu arayabilirsin.';
            kicker.textContent = data.action === 'unavailable' ? 'SAAT DEĞİŞMİŞ' : data.action === 'profile' ? 'SON BİR ADIM' : 'GİRİŞ TAMAM';
            title.textContent = data.action === 'unavailable' ? 'Bu saat artık seçilemiyor.' : data.action === 'profile' ? 'Profilini tamamla.' : 'Maçın buradan başlar.';
            content.innerHTML = `<div class="pl-ticket pl-resume-selection">${ticketHtml({ label: 'Seçtiğin maç saati', title: data.companyName || '', icon: 'fa-futbol',
                rows: [['Saha', data.fieldName || ''], ['Gün', data.date ? formatDay(data.date) : ''], ['Saat', data.time?.slice(0, 5) || '']] })}</div><p class="pl-resume-copy">${escape(description)}</p>`;
            if (data.action !== 'unavailable') content.insertAdjacentHTML('beforeend', `<ul class="pl-resume-benefits"><li><i class="fa-solid fa-futbol" aria-hidden="true"></i>Ücretsiz maç ilanı</li><li><i class="fa-solid fa-users" aria-hidden="true"></i>Takım ve oyuncular</li><li><i class="fa-solid fa-chart-line" aria-hidden="true"></i>Maç istatistikleri</li></ul><button type="button" class="pl-btn pl-btn--lime" data-pl-continue><em>${data.action === 'profile' ? 'Profilini tamamla, rezervasyona devam et' : 'Devam et, maçını planla'}</em><i class="fa-solid fa-arrow-right" aria-hidden="true"></i></button>`);
            dialog.showModal();
            content.addEventListener('click', async event => {
                const button = event.target.closest('[data-pl-continue]');
                if (!button || button.disabled) return;
                button.disabled = true; button.classList.add('is-loading');
                try { const result = await request(state, '/Field/ContinueLanding', {}, 'POST'); state.continuing = true; location.assign(result.redirectUrl); }
                catch (error) { button.disabled = false; button.classList.remove('is-loading'); content.querySelector('.pl-resume-error')?.remove(); content.insertAdjacentHTML('beforeend', `<p class="pl-resume-error" role="alert"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>${escape(error.message)}</p>`); }
            }, { signal: state.events.signal });
            dialog.addEventListener('close', () => { if (!state.continuing) consume(); }, { signal: state.events.signal, once: true });
        } catch (error) {
            if (!active(state)) return;
            kicker.textContent = 'BİR SORUN OLDU'; title.textContent = 'Seçimine devam edemedik.';
            content.innerHTML = `<p class="pl-resume-error" role="alert"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>${escape(error.message)}</p><button class="pl-btn pl-btn--lime" type="button" data-pl-resume-retry><em>Yeniden dene</em></button>`;
            content.querySelector('button').addEventListener('click', () => resume(state), { once: true });
            if (!dialog.open) dialog.showModal();
        }
    }
    // Light, GPU-friendly motion: scroll state, floodlight pointer glow and sticky action visibility.
    function enhance(state) {
        const root = state.root, signal = state.events.signal;
        state.observers = [];
        const hero = root.querySelector('[data-pl-hero]'), dock = root.querySelector('[data-pl-dock]');
        const onScroll = () => { state.frame = 0; root.classList.toggle('is-scrolled', window.scrollY > 24); };
        window.addEventListener('scroll', () => { if (!state.frame) state.frame = requestAnimationFrame(onScroll); }, { signal, passive: true });
        onScroll();
        if ('IntersectionObserver' in window) {
            const away = new IntersectionObserver(entries => entries.forEach(entry => root.classList.toggle('is-away', !entry.isIntersecting)), { threshold: 0 });
            away.observe(hero); state.observers.push(away);
        }
        dock?.classList.add('is-visible');
        if (!reducedMotion.matches && finePointer.matches) {
            let frame = 0;
            hero.addEventListener('pointermove', event => {
                if (frame) return;
                frame = requestAnimationFrame(() => {
                    frame = 0; const rect = hero.getBoundingClientRect();
                    hero.style.setProperty('--mx', ((event.clientX - rect.left) / rect.width * 100).toFixed(1) + '%');
                    hero.style.setProperty('--my', ((event.clientY - rect.top) / rect.height * 100).toFixed(1) + '%');
                });
            }, { signal, passive: true });
        }
    }
    async function initialize() {
        const root = document.querySelector('[data-potential-landing],[data-facility-landing-resume],[data-profile-landing],[data-profile-landing-resume],[data-news-landing]');
        if (!root) { dispose(); delete document.documentElement.dataset.potentialPlatform; return; }
        if (!await window.isPotentialCompanyBrowser()) { dispose(); return; }
        if (!root.isConnected) return;
        document.documentElement.dataset.potentialPlatform = 'web';
        if (current?.root === root) return;
        // Do not mark a profile initialized until its action handlers are available.
        // The readiness event retries initialization if this script loads first.
        if (root.hasAttribute('data-profile-landing') && typeof window.setupProfileLanding !== 'function') return;
        if (root.hasAttribute('data-profile-landing-resume') && typeof window.resumeProfileLanding !== 'function') return;
        dispose();
        const state = current = { root, events: new AbortController() };
        setupDialogs(state);
        root.querySelector('[data-landing-menu]')?.addEventListener('click', event => {
            const menu = event.currentTarget;
            const expanded = root.classList.toggle('pl-nav-menu-open');
            menu.setAttribute('aria-expanded', String(expanded));
            menu.setAttribute('aria-label', expanded ? 'Menüyü kapat' : 'Menüyü aç');
        }, { signal: state.events.signal });
        if (root.hasAttribute('data-news-landing')) { enhance(state); followProfileFragment(state); return; }
        if (root.hasAttribute('data-profile-landing-resume')) { await window.resumeProfileLanding(state, request); return; }
        if (root.hasAttribute('data-profile-landing')) { window.setupProfileLanding(state, intent => openAuth(state, intent, { label: 'Seçtiğin işlem', title: root.querySelector('h1').textContent.replace(/\.$/, ''), icon: 'fa-futbol', rows: [['İşlem', { profile: 'Profili incele', invite: 'Maça davet et', chat: 'Sohbet başlat', join: 'Takıma katıl', follow: 'Takip et' }[intent.action]]] })); enhance(state); followProfileFragment(state); return; }
        if (root.hasAttribute('data-facility-landing-resume')) { await resume(state); return; }
        state.facilityName = (root.querySelector('h1')?.firstChild?.textContent || root.querySelector('h1').textContent).trim();
        if (!reducedMotion.matches && 'IntersectionObserver' in window) {
            root.classList.add('pl-motion');
            state.reveal = new IntersectionObserver(entries => entries.forEach(entry => {
                if (entry.isIntersecting) { entry.target.classList.add('is-visible'); state.reveal.unobserve(entry.target); }
            }), { threshold: .08 });
            root.querySelectorAll('[data-pl-reveal]').forEach(element => state.reveal.observe(element));
        }
        enhance(state);
        window.addEventListener('resize', () => { state.slotPageSize = null; fitCalendar(state); }, { signal: state.events.signal, passive: true });
        document.fonts?.ready.then(() => { state.slotPageSize = null; fitCalendar(state); });
        root.addEventListener('click', event => {
            const slotPage = event.target.closest('[data-pl-slot-page]');
            if (slotPage && !slotPage.disabled) { state.slotPage = Number(slotPage.dataset.plSlotPage); fitCalendar(state); }
            if (event.target.closest('[data-pl-visitor-summary]')) scrollToTarget(root, '.pl-footer');
            if (event.target.closest('[data-pl-directions]')) {
                event.preventDefault();
                openAuth(state, { id: root.dataset.companyId, action: 'directions' }, {
                    label: 'Yol tarifi', title: state.facilityName, icon: 'fa-route'
                });
            }
            if (event.target.closest('[data-pl-contact]')) openAuth(state, { id: root.dataset.companyId, action: 'contact' }, {
                label: 'Tesisle iletişim', title: state.facilityName, icon: 'fa-phone'
            });
            if (event.target.closest('[data-pl-calendar-retry]')) loadCalendar(state);
            const day = event.target.closest('[data-pl-day]');
            if (day) { root.querySelectorAll('[data-pl-day]').forEach(button => button.setAttribute('aria-pressed', String(button === day))); loadCalendar(state); }
            if (event.target.closest('[data-pl-next-day]')) {
                const next = root.querySelector('[data-pl-day][aria-pressed="true"]')?.nextElementSibling;
                if (next) { next.click(); next.focus({ preventScroll: true }); }
            }
            const field = event.target.closest('[data-pl-field-choice]');
            if (field && field.getAttribute('aria-pressed') !== 'true') { chooseField(state, field); loadCalendar(state); }
            const pick = event.target.closest('[data-pl-pick-field]');
            if (pick) {
                const choice = root.querySelector(`[data-pl-field-choice="${CSS.escape(pick.dataset.plPickField)}"]`);
                if (choice && choice.getAttribute('aria-pressed') !== 'true') { chooseField(state, choice); loadCalendar(state); }
                scrollToTarget(root, '#pl-calendar');
            }
            const anchor = event.target.closest('a[href^="#pl-"]');
            if (anchor && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                const fragment = anchor.getAttribute('href');
                if (root.querySelector(fragment)) {
                    event.preventDefault();
                    history.replaceState(history.state, '', fragment);
                    scrollToTarget(root, fragment);
                }
            }
        }, { signal: state.events.signal });
        root.querySelector('.pl-cover')?.addEventListener('error', event => {
            const fallback = document.createElement('div'); fallback.className = 'pl-pitch'; fallback.setAttribute('aria-hidden', 'true');
            fallback.innerHTML = '<div class="pl-pitch-lines"><span></span></div><div class="pl-pitch-ball"></div>';
            event.target.closest('picture').replaceWith(fallback);
        }, { signal: state.events.signal });
        await loadCalendar(state);
        followProfileFragment(state);
    }
    // The landing becomes visible after the shared web/native platform decision.
    // Reapply only our section fragments; never turn a URL fragment into an auth action.
    function followProfileFragment(state) {
        const fragment = window.location.hash.slice(1);
        if (!/^(pl|profile)-[a-z-]+$/.test(fragment)) return;
        requestAnimationFrame(() => {
            if (current !== state || !state.root.isConnected) return;
            const target = document.getElementById(fragment);
            if (!target || !state.root.contains(target)) return;
            target.scrollIntoView({ behavior: 'instant', block: 'start' });
            target.focus?.({ preventScroll: true });
        });
    }
    function scrollToTarget(root, selector) {
        const target = root.querySelector(selector);
        if (!target) return;
        target.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
        // Keyboard and screen-reader users land inside the section, not just visually near it.
        target.focus?.({ preventScroll: true });
    }
    window.initPotentialCompanyLanding = initialize;
    reducedMotion.addEventListener('change', () => { dispose(); initialize(); });
    const countRequests = new Map();
    const visitorNumber = new Intl.NumberFormat('tr-TR');
    const visitorDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' });
    function todayInTurkey() {
        const parts = Object.fromEntries(visitorDate.formatToParts(new Date()).map(part => [part.type, part.value]));
        return `${parts.year}-${parts.month}-${parts.day}`;
    }
    function fetchVisitorCount(url) {
        if (countRequests.has(url)) return countRequests.get(url);
        const pending = (async () => {
            for (let attempt = 0; attempt < 3; attempt++) {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 6500);
                try {
                    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
                        headers: { 'X-Requested-With': 'XMLHttpRequest' } });
                    if (!response.ok || response.redirected) throw new Error('Visitor count unavailable');
                    const data = await response.json();
                    if (!Number.isSafeInteger(data?.count) || data.count < 0 || typeof data.date !== 'string' ||
                        data.date.slice(0, 10) !== todayInTurkey()) throw new Error('Invalid visitor count');
                    return data.count;
                } catch (error) {
                    if (attempt === 2) throw error;
                } finally { clearTimeout(timeout); }
                await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 1000));
            }
        })().finally(() => countRequests.delete(url));
        countRequests.set(url, pending);
        return pending;
    }
    let visitorBadges = new WeakSet();
    function loadVisitorCounts() {
        const groups = new Map();
        document.querySelectorAll('[data-facility-visitors]').forEach(badge => {
            if (visitorBadges.has(badge)) return;
            visitorBadges.add(badge);
            const id = badge.dataset.facilityVisitors;
            if (!/^[1-9]\d*$/.test(id)) return;
            if (!groups.has(id)) groups.set(id, []);
            groups.get(id).push(badge);
        });
        groups.forEach((badges, id) => {
            fetchVisitorCount('/Field/LandingVisitors?id=' + id)
                .then(count => {
                    badges.filter(badge => badge.isConnected).forEach(badge => {
                        badge.textContent = 'Bugün ' + visitorNumber.format(count) + (badge.hasAttribute('data-visitors-compact') ? ' ziyaretçi' : ' tekil ziyaretçi');
                        badge.hidden = false;
                    });
                }).catch(() => { badges.forEach(badge => badge.hidden = true); });
        });
    }
    let onlineBadges = new WeakSet();
    function loadOnlineCount() {
        const badges = Array.from(document.querySelectorAll('[data-pl-online-count]')).filter(badge => !onlineBadges.has(badge));
        if (!badges.length) return;
        badges.forEach(badge => onlineBadges.add(badge));
        fetchVisitorCount('/Field/LandingOnlineVisitors')
            .then(count => {
                badges.filter(badge => badge.isConnected).forEach(badge => {
                    badge.textContent = visitorNumber.format(count);
                    const summary = badge.closest('[data-pl-visitor-summary]');
                    if (summary) { summary.hidden = false; summary.setAttribute('aria-label', `Bugün ${visitorNumber.format(count)} tekil ziyaretçi`); }
                });
            }).catch(() => {
                // Hide unavailable or stale totals; never leave a dash or invent a zero.
                badges.forEach(badge => { const summary = badge.closest('[data-pl-visitor-summary]'); if (summary) summary.hidden = true; });
            });
    }
    function refreshVisitorCounts() {
        if (document.hidden) return;
        visitorBadges = new WeakSet(); onlineBadges = new WeakSet();
        loadOnlineCount(); loadVisitorCounts();
    }
    function start() {
        initialize();
        loadOnlineCount();
        loadVisitorCounts();
        const host = document.getElementById('_partialBody');
        if (host) new MutationObserver(() => { initialize(); loadVisitorCounts(); loadOnlineCount(); }).observe(host, { childList: true });
    }
    window.addEventListener('profile-landing:ready', initialize);
    window.addEventListener('online', refreshVisitorCounts);
    window.addEventListener('pageshow', event => { if (event.persisted) refreshVisitorCounts(); });
    document.addEventListener('visibilitychange', refreshVisitorCounts);
    setInterval(refreshVisitorCounts, 30000);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();

// Restore the chosen facility/day/hour in the existing match organizer, including its create-team step.
window.applyFacilityLandingMatchSelection = selection => {
    const body = document.querySelector('[data-create-match-premium]')?.closest('.custom-modal-body');
    if (!body) return;
    body.querySelector('#DynamicLocationCompanies').value = String(selection.companyId);
    body.querySelector('#DynamicCityId').value = String(selection.cityId);
    body.querySelector('#DynamicDistrictId').value = String(selection.districtId);
    body.querySelector('#FieldCalendarInput').value = selection.logicalDate;
    const [year, month, day] = selection.logicalDate.split('-');
    for (const [id, value] of [['Day', String(Number(day))], ['Month', month], ['Year', year]]) {
        const input = body.querySelector('#' + id); if (input) input.value = value;
    }
    body.querySelectorAll('.create-match-quick-date').forEach(button => button.classList.toggle('is-selected', button.dataset.date === selection.logicalDate));
    window.updateCreateMatchDateSummary?.(selection.logicalDate);
    const observer = new MutationObserver(() => {
        const section = body.querySelector(`#innerFieldSelectiveGroup_${Number(selection.fieldId)}`);
        const slot = section && [...section.querySelectorAll('.OpenMatchOrganizerToFields')].find(element =>
            element.dataset.time === selection.time && element.dataset.date === selection.date);
        if (!slot) return;
        observer.disconnect(); clearTimeout(timeout);
        body.querySelector(`[global-tab-target="innerFieldSelectiveGroup_${Number(selection.fieldId)}"]`)?.click();
        slot.click();
    });
    observer.observe(body, { childList: true, subtree: true });
    const timeout = setTimeout(() => observer.disconnect(), 15000);
};
